import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  ArrowLeft, Bookmark, Briefcase, Calendar, ChevronDown, FileText,
  MapPin, MoreHorizontal, Pencil, Search, Share2, StickyNote,
} from 'lucide-react'
import {
  getMyJobs, getMyJobApplications, getMyJobApplicationDetail, setJobApplicationStatus,
  setJobApplicationSaved, setJobApplicationNote, getColorFromName, getSkillTags, WORK_TYPE_LABELS,
  EXPERIENCE_RANGE_LABELS, APPLICATION_STATUS_LABELS, COMPANY_SETTABLE_STATUSES,
  type ApiJobListItem, type ApiCompanyApplication, type CompanySettableStatus, type ApiSkillTag,
  downloadApplicationDocument,
} from '@/services/jobService'
import { RowMenu } from './JobPostingsTab'
import { getSkillIcon } from './skillIcons'
import { useProvinces, normalizeProvince } from '@/services/addressService'

// หน้านี้ต่อ backend จริงแล้ว (2026-09-29) — ก่อนหน้านี้เป็น mock ทั้งหน้า
// ตัดสินใจแล้วว่าไม่ทำ: นัดสัมภาษณ์แบบมีวัน-เวลา (t_job_interview) และประวัติการติดต่อ (t_job_application_event)
// — INTERVIEWED เป็นแค่สถานะ ไม่มีการนัดผูกไว้ / เอกสารแนบยังเป็น null ทั้งหมด (รอต่อ MinIO)

const STATUS_PILL: Record<string, string> = {
  APPLIED: 'bg-accent/10 text-accent',
  REVIEWED: 'bg-[#F1F0F7] text-[#69738A]',
  INTERVIEWED: 'bg-amber-50 text-amber-700',
  OFFERED: 'bg-emerald-50 text-emerald-700',
  REJECTED: 'bg-rose-50 text-rose-600',
  WITHDRAWN: 'bg-rose-50 text-rose-600',
}

const formatDateTime = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleDateString('th-TH', { day: 'numeric', month: 'short', year: 'numeric' }) +
      ' · ' + new Date(iso).toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit' })
    : '-'

const formatMonthYear = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString('th-TH', { month: 'short', year: 'numeric' }) : ''

const PAGE_SIZE = 8
const DETAIL_TABS = [
  { key: 'info', label: 'ข้อมูลผู้สมัคร' },
  { key: 'learning', label: 'สกิลและการอบรม' },
  { key: 'assessment', label: 'ผลการประเมิน' },
  { key: 'documents', label: 'เอกสารแนบ' },
] as const
type DetailTabKey = typeof DETAIL_TABS[number]['key']

export default function JobApplicantsView({ jobId }: { jobId: number }) {
  const navigate = useNavigate()
  const [job, setJob] = useState<ApiJobListItem | null>(null)
  const [applications, setApplications] = useState<ApiCompanyApplication[]>([])
  const [skillTags, setSkillTags] = useState<ApiSkillTag[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState('')

  const [listTab, setListTabRaw] = useState<'all' | 'saved'>('all')
  const [search, setSearchRaw] = useState('')
  const [statusFilter, setStatusFilterRaw] = useState('')
  const [expFilter, setExpFilterRaw] = useState('')
  const [provinceFilter, setProvinceFilterRaw] = useState('')
  const { data: provinces } = useProvinces()
  const [skillFilter, setSkillFilterRaw] = useState<string[]>([])
  const [salaryMin, setSalaryMinRaw] = useState('')
  const [salaryMax, setSalaryMaxRaw] = useState('')
  const [scoreMin, setScoreMinRaw] = useState('')
  const [sort, setSortRaw] = useState<'latest' | 'score'>('latest')
  const [page, setPage] = useState(1)
  const [showMoreFilters, setShowMoreFilters] = useState(false)
  // เปลี่ยนตัวกรอง/ค้นหา/แท็บ/เรียงลำดับแล้วกลับไปหน้า 1 เสมอ — รีเซ็ตตรงจุดที่ผู้ใช้กดแทนการใช้ effect
  const setListTab = (v: typeof listTab) => { setListTabRaw(v); setPage(1) }
  const setSearch = (v: string) => { setSearchRaw(v); setPage(1) }
  const setStatusFilter = (v: string) => { setStatusFilterRaw(v); setPage(1) }
  const setExpFilter = (v: string) => { setExpFilterRaw(v); setPage(1) }
  const setProvinceFilter = (v: string) => { setProvinceFilterRaw(v); setPage(1) }
  const setSalaryMin = (v: string) => { setSalaryMinRaw(v); setPage(1) }
  const setSalaryMax = (v: string) => { setSalaryMaxRaw(v); setPage(1) }
  const setScoreMin = (v: string) => { setScoreMinRaw(v); setPage(1) }
  const toggleSkillFilter = (skill: string) => {
    setSkillFilterRaw((prev) => (prev.includes(skill) ? prev.filter((s) => s !== skill) : [...prev, skill]))
    setPage(1)
  }
  const setSort = (v: typeof sort) => { setSortRaw(v); setPage(1) }
  const clearMoreFilters = () => {
    setExpFilterRaw(''); setProvinceFilterRaw(''); setSkillFilterRaw([]); setSalaryMinRaw(''); setSalaryMaxRaw(''); setScoreMinRaw('')
    setPage(1)
  }
  const moreFiltersCount = (expFilter ? 1 : 0) + (provinceFilter ? 1 : 0) + skillFilter.length + (salaryMin ? 1 : 0) + (salaryMax ? 1 : 0) + (scoreMin ? 1 : 0)

  const [activeId, setActiveId] = useState<number | null>(null)
  const [rowMenu, setRowMenu] = useState<{ id: number; rect: DOMRect } | null>(null)
  const [headerMenu, setHeaderMenu] = useState<DOMRect | null>(null)
  const [detailTab, setDetailTab] = useState<DetailTabKey>('info')
  const [toast, setToast] = useState('')
  const [busyId, setBusyId] = useState<number | null>(null)

  useEffect(() => {
    Promise.resolve().then(() => setLoading(true))
    Promise.all([
      getMyJobs().then((list) => list.find((j) => j.job_id === jobId) ?? null),
      getMyJobApplications(jobId),
      getSkillTags().catch(() => []),
    ])
      .then(([jobData, apps, tags]) => {
        setJob(jobData)
        setApplications(apps)
        setSkillTags(tags)
        setActiveId(apps[0]?.application_id ?? null)
      })
      .catch((err) => setLoadError(err instanceof Error ? err.message : 'โหลดข้อมูลไม่สำเร็จ'))
      .finally(() => setLoading(false))
  }, [jobId])

  useEffect(() => {
    if (!toast) return
    const t = setTimeout(() => setToast(''), 2200)
    return () => clearTimeout(t)
  }, [toast])

  // เปิดดูครั้งแรก (ยังเป็น APPLIED) → server เปลี่ยนเป็น REVIEWED ให้ — sync กลับเข้า state list
  useEffect(() => {
    if (activeId === null) return
    const current = applications.find((a) => a.application_id === activeId)
    if (!current || (current.status !== 'APPLIED' && current.learning !== undefined)) return
    getMyJobApplicationDetail(jobId, activeId)
      .then((detail) => setApplications((prev) => prev.map((a) => (a.application_id === detail.application_id ? detail : a))))
      .catch(() => setApplications((prev) => prev.map((a) => (a.application_id === activeId ? { ...a, learning: null } : a))))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId])

  const toggleSaved = (a: ApiCompanyApplication) => {
    setBusyId(a.application_id)
    setJobApplicationSaved(jobId, a.application_id, !a.company_saved)
      .then((updated) => setApplications((prev) => prev.map((x) => (x.application_id === updated.application_id ? updated : x))))
      .catch((err) => setToast(err instanceof Error ? err.message : 'บันทึกไม่สำเร็จ'))
      .finally(() => setBusyId(null))
  }

  const changeStatus = (a: ApiCompanyApplication, status: CompanySettableStatus) => {
    setBusyId(a.application_id)
    setJobApplicationStatus(jobId, a.application_id, status)
      .then((updated) => {
        setApplications((prev) => prev.map((x) => (x.application_id === updated.application_id ? updated : x)))
        setToast(`เปลี่ยนสถานะ ${a.applicant_name} เป็น "${APPLICATION_STATUS_LABELS[status]}" แล้ว`)
      })
      .catch((err) => setToast(err instanceof Error ? err.message : 'เปลี่ยนสถานะไม่สำเร็จ'))
      .finally(() => setBusyId(null))
  }

  // ตัวเลือก filter ใช้ master list เสมอ (77 จังหวัด / skill tags ทั้งหมด) — ไม่จำกัดแค่ค่าที่มีคนสมัครจริง
  // เพื่อให้บริษัทกรองหา "ช่วงที่ต้องการ" ล่วงหน้าได้ แม้ยังไม่มีผู้สมัครตรงเงื่อนไขนั้นเลย
  const skillOptions = useMemo(() => skillTags.map((t) => t.name).sort((a, b) => a.localeCompare(b, 'th')), [skillTags])

  const filtered = useMemo(() => {
    let list = applications
    if (listTab === 'saved') list = list.filter((a) => a.company_saved)
    const q = search.trim().toLowerCase()
    if (q) list = list.filter((a) => a.applicant_name.toLowerCase().includes(q) || (a.skills ?? []).some((s) => s.toLowerCase().includes(q)))
    if (statusFilter) list = list.filter((a) => a.status === statusFilter)
    if (expFilter) list = list.filter((a) => a.experience_range === expFilter)
    if (provinceFilter) list = list.filter((a) => normalizeProvince(a.province) === provinceFilter)
    if (skillFilter.length > 0) list = list.filter((a) => skillFilter.every((s) => (a.skills ?? []).includes(s)))
    const minSalary = salaryMin ? Number(salaryMin) : null
    if (minSalary !== null) list = list.filter((a) => a.expected_salary != null && a.expected_salary >= minSalary)
    const maxSalary = salaryMax ? Number(salaryMax) : null
    if (maxSalary !== null) list = list.filter((a) => a.expected_salary != null && a.expected_salary <= maxSalary)
    const minScore = scoreMin ? Number(scoreMin) : null
    if (minScore !== null) list = list.filter((a) => a.assessment_score?.score != null && a.assessment_score.score >= minScore)
    list = [...list]
    if (sort === 'score') list.sort((a, b) => (b.assessment_score?.score ?? -1) - (a.assessment_score?.score ?? -1))
    else list.sort((a, b) => new Date(b.applied_at).getTime() - new Date(a.applied_at).getTime())
    return list
  }, [applications, listTab, search, statusFilter, expFilter, provinceFilter, skillFilter, salaryMin, salaryMax, scoreMin, sort])

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)
  const active = applications.find((a) => a.application_id === activeId) ?? null
  const savedCount = applications.filter((a) => a.company_saved).length

  if (loading) {
    return <div className="flex items-center justify-center rounded-2xl border border-dashed border-[#E1DCF5] bg-[#FAFAFF] py-20 text-sm text-[#69738A]">กำลังโหลด...</div>
  }
  if (loadError || !job) {
    return (
      <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-[#E1DCF5] bg-[#FAFAFF] px-6 py-20 text-center">
        <p className="text-sm text-[#69738A]">{loadError || 'ไม่พบตำแหน่งงานนี้ หรือคุณไม่มีสิทธิ์เข้าถึง'}</p>
        <Link to="/account?tab=job-postings" className="mt-4 text-sm font-medium text-accent hover:underline">
          กลับไปหน้าประกาศงาน
        </Link>
      </div>
    )
  }

  return (
    <div>
      {toast && <div className="mb-4 rounded-xl border border-accent/20 bg-accent/5 px-4 py-2.5 text-sm text-accent">{toast}</div>}

      {/* ── Header ── */}
      <Link to="/account?tab=job-postings" className="mb-3 inline-flex items-center gap-1.5 text-sm text-[#69738A] hover:text-accent">
        <ArrowLeft className="h-3.5 w-3.5" /> กลับไปหน้าประกาศงาน
      </Link>
      <div className="mb-5 flex flex-wrap items-start justify-between gap-4 border-b border-[#E7E7ED] pb-5">
        <div>
          <div className="flex flex-wrap items-center gap-2.5">
            <h1 className="text-2xl font-semibold text-[#19172A]">{job.title}</h1>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700">
              {job.status === 'ACTIVE' ? 'กำลังเปิดรับ' : job.status === 'DRAFT' ? 'แบบร่าง' : 'ปิดรับสมัคร'}
            </span>
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-[#69738A]">
            <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {job.location}</span>
            <span className="flex items-center gap-1"><Briefcase className="h-3.5 w-3.5" /> {job.is_remote ? 'Remote' : WORK_TYPE_LABELS[job.work_type] || job.work_type}</span>
            <span className="flex items-center gap-1"><Calendar className="h-3.5 w-3.5" /> เผยแพร่เมื่อ {job.published_at ? formatDateTime(job.published_at).split(' ·')[0] : '-'}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate(`/post-job?edit=${job.job_id}`)}
            className="flex items-center gap-1.5 rounded-lg border border-[#E7E5FF] px-3.5 py-2 text-sm font-medium text-[#19172A] transition hover:border-accent hover:text-accent"
          >
            <Pencil className="h-3.5 w-3.5" /> แก้ไขประกาศงาน
          </button>
          <button
            onClick={() => { navigator.clipboard?.writeText(`${window.location.origin}/jobs/${job.job_id}`); setToast('คัดลอกลิงก์ตำแหน่งงานแล้ว') }}
            className="flex items-center gap-1.5 rounded-lg border border-[#E7E5FF] px-3.5 py-2 text-sm font-medium text-[#19172A] transition hover:border-accent hover:text-accent"
          >
            <Share2 className="h-3.5 w-3.5" /> แชร์ตำแหน่งงาน
          </button>
          <button
            onClick={(e) => setHeaderMenu(headerMenu ? null : e.currentTarget.getBoundingClientRect())}
            className="flex h-9 w-9 items-center justify-center rounded-lg border border-[#E7E5FF] text-[#69738A] transition hover:border-accent hover:text-accent"
          >
            <MoreHorizontal className="h-4 w-4" />
          </button>
          {headerMenu && (
            <RowMenu anchorRect={headerMenu} onClose={() => setHeaderMenu(null)}>
              <Link to={`/jobs/${job.job_id}`} onClick={() => setHeaderMenu(null)} className="block px-3.5 py-2 text-sm text-[#19172A] hover:bg-[#F5F3FC]">
                ดูประกาศสาธารณะ
              </Link>
            </RowMenu>
          )}
        </div>
      </div>

      {/* ── Applicant tabs ── */}
      <div className="mb-5 flex gap-6 border-b border-[#E7E7ED]">
        {[
          { key: 'all' as const, label: 'ผู้สมัครทั้งหมด', count: applications.length },
          { key: 'saved' as const, label: 'บันทึกไว้', count: savedCount },
        ].map((t) => (
          <button
            key={t.key}
            onClick={() => setListTab(t.key)}
            className={`border-b-2 pb-3 text-sm font-medium transition-colors ${
              listTab === t.key ? 'border-accent text-accent' : 'border-transparent text-[#69738A] hover:text-[#19172A]'
            }`}
          >
            {t.label} <span className="text-xs text-[#69738A]">({t.count})</span>
          </button>
        ))}
      </div>

      {/* ── 2-column workspace ── */}
      <div className="grid grid-cols-1 gap-5 xl:grid-cols-[1fr_1.05fr]">
        {/* LEFT — list */}
        <div>
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <div className="relative min-w-[180px] flex-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[#69738A]" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="ค้นหาชื่อผู้สมัคร, ทักษะ..."
                className="w-full rounded-lg border border-[#E7E5FF] bg-white py-2 pl-9 pr-3 text-xs text-[#19172A] outline-none focus:border-accent"
              />
            </div>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
              className="rounded-lg border border-[#E7E5FF] bg-white px-2.5 py-2 text-xs text-[#19172A] outline-none">
              <option value="">ทุกสถานะ</option>
              {Object.entries(APPLICATION_STATUS_LABELS).filter(([k]) => k !== 'WITHDRAWN').map(([k, label]) => (
                <option key={k} value={k}>{label}</option>
              ))}
            </select>
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value as typeof sort)}
              className="rounded-lg border border-[#E7E5FF] bg-white px-2.5 py-2 text-xs text-[#19172A] outline-none"
            >
              <option value="latest">เรียงตาม: ล่าสุด</option>
              <option value="score">เรียงตาม: ผลประเมิน</option>
            </select>
            <button
              onClick={() => setShowMoreFilters((v) => !v)}
              className={`flex items-center gap-1.5 rounded-lg border px-2.5 py-2 text-xs font-medium transition ${
                showMoreFilters || moreFiltersCount > 0 ? 'border-accent text-accent' : 'border-[#E7E5FF] text-[#69738A] hover:border-accent hover:text-accent'
              }`}
            >
              ตัวกรองเพิ่มเติม {moreFiltersCount > 0 && `(${moreFiltersCount})`}
              <ChevronDown className={`h-3.5 w-3.5 transition-transform ${showMoreFilters ? 'rotate-180' : ''}`} />
            </button>
          </div>

          {showMoreFilters && (
            <div className="mb-3 rounded-xl border border-[#E7E5FF] bg-[#FAFAFF] p-3.5">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <div>
                  <label className="mb-1 block text-[11px] font-medium text-[#9691AC]">ประสบการณ์</label>
                  <select value={expFilter} onChange={(e) => setExpFilter(e.target.value)}
                    className="w-full rounded-lg border border-[#E7E5FF] bg-white px-2.5 py-1.5 text-xs text-[#19172A] outline-none">
                    <option value="">ทุกประสบการณ์</option>
                    {Object.entries(EXPERIENCE_RANGE_LABELS).map(([k, label]) => (
                      <option key={k} value={k}>{label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-[11px] font-medium text-[#9691AC]">จังหวัด</label>
                  <select value={provinceFilter} onChange={(e) => setProvinceFilter(e.target.value)}
                    className="w-full rounded-lg border border-[#E7E5FF] bg-white px-2.5 py-1.5 text-xs text-[#19172A] outline-none">
                    <option value="">ทุกจังหวัด</option>
                    {provinces.map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-[11px] font-medium text-[#9691AC]">เงินเดือนที่คาดหวัง (฿)</label>
                  <div className="flex items-center gap-1.5">
                    <input type="number" min={0} value={salaryMin} onChange={(e) => setSalaryMin(e.target.value)} placeholder="ต่ำสุด"
                      className="w-full rounded-lg border border-[#E7E5FF] bg-white px-2.5 py-1.5 text-xs text-[#19172A] outline-none focus:border-accent" />
                    <span className="text-xs text-[#9691AC]">-</span>
                    <input type="number" min={0} value={salaryMax} onChange={(e) => setSalaryMax(e.target.value)} placeholder="สูงสุด"
                      className="w-full rounded-lg border border-[#E7E5FF] bg-white px-2.5 py-1.5 text-xs text-[#19172A] outline-none focus:border-accent" />
                  </div>
                </div>
                <div>
                  <label className="mb-1 block text-[11px] font-medium text-[#9691AC]">คะแนนแบบประเมิน (ขั้นต่ำ)</label>
                  <input type="number" min={0} value={scoreMin} onChange={(e) => setScoreMin(e.target.value)} placeholder="เช่น 15"
                    className="w-full rounded-lg border border-[#E7E5FF] bg-white px-2.5 py-1.5 text-xs text-[#19172A] outline-none focus:border-accent" />
                </div>
                <div>
                  <label className="mb-1 block text-[11px] font-medium text-[#9691AC]">ล้างตัวกรองเพิ่มเติม</label>
                  <button onClick={clearMoreFilters} disabled={moreFiltersCount === 0}
                    className="w-full rounded-lg border border-[#E7E5FF] bg-white px-2.5 py-1.5 text-xs font-medium text-[#69738A] transition hover:border-accent hover:text-accent disabled:opacity-40">
                    ล้างทั้งหมด
                  </button>
                </div>
              </div>
              {skillOptions.length > 0 && (
                <div className="mt-3">
                  <label className="mb-1.5 block text-[11px] font-medium text-[#9691AC]">ทักษะ</label>
                  <div className="flex flex-wrap gap-1.5">
                    {skillOptions.map((s) => {
                      const selected = skillFilter.includes(s)
                      return (
                        <button
                          key={s}
                          onClick={() => toggleSkillFilter(s)}
                          className={`rounded-full border px-2.5 py-1 text-[11px] font-medium transition ${
                            selected ? 'border-accent bg-accent/10 text-accent' : 'border-[#E7E5FF] bg-white text-[#69738A] hover:border-accent hover:text-accent'
                          }`}
                        >
                          {s}
                        </button>
                      )
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="overflow-hidden rounded-2xl border border-[#E7E5FF] bg-white">
            {pageItems.length === 0 ? (
              <div className="p-10 text-center text-sm text-[#69738A]">
                {applications.length === 0 ? 'ยังไม่มีผู้สมัครตำแหน่งนี้' : 'ไม่พบผู้สมัครที่ตรงกับเงื่อนไข'}
              </div>
            ) : (
              <ul className="divide-y divide-[#F0EEFB]">
                {pageItems.map((a) => {
                  const isActive = a.application_id === activeId
                  return (
                    <li
                      key={a.application_id}
                      onClick={() => { setActiveId(a.application_id); setDetailTab('info') }}
                      className={`flex cursor-pointer items-center gap-3 px-4 py-3 transition ${isActive ? 'bg-accent/5' : 'hover:bg-[#FAFAFF]'} ${busyId === a.application_id ? 'opacity-60' : ''}`}
                    >
                      <div
                        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-semibold text-white"
                        style={{ background: getColorFromName(a.applicant_name) }}
                      >
                        {a.applicant_name[0]}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <p className="truncate text-sm font-semibold text-[#19172A]">{a.applicant_name}</p>
                          <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium ${STATUS_PILL[a.status]}`}>{APPLICATION_STATUS_LABELS[a.status] ?? a.status}</span>
                        </div>
                        <ul className="mt-1.5 space-y-1 text-xs text-[#69738A]">
                          <li className="flex items-center gap-2">
                            <MapPin className="h-3.5 w-3.5 shrink-0 text-[#A7A3C9]" />
                            <span className="truncate">{a.province || 'ไม่ระบุจังหวัด'}</span>
                          </li>
                          <li className="flex items-center gap-2">
                            <Briefcase className="h-3.5 w-3.5 shrink-0 text-[#A7A3C9]" />
                            <span className="truncate">{a.experience_range ? EXPERIENCE_RANGE_LABELS[a.experience_range] ?? a.experience_range : 'ไม่ระบุประสบการณ์'}</span>
                          </li>
                          <li className="flex items-center gap-2 text-[#9691AC]">
                            <Calendar className="h-3.5 w-3.5 shrink-0 text-[#C9C5E8]" />
                            <span className="truncate">สมัครเมื่อ {formatDateTime(a.applied_at)}</span>
                          </li>
                        </ul>
                      </div>
                      {/* ผลประเมิน — เป็นข้อมูลรอง ไม่ใช่ตัวชี้วัดหลัก จึงเล็กและสีเรียบ ไม่ใช้สีเด่น */}
                      <div className="hidden shrink-0 flex-col items-end gap-1 sm:flex">
                        {a.assessment_score && (
                          <span className="text-[11px] text-[#69738A]">{a.assessment_score.score}/{a.assessment_score.total}</span>
                        )}
                        <button onClick={(e) => { e.stopPropagation(); toggleSaved(a) }} className="text-[#69738A] hover:text-accent">
                          <Bookmark className={`h-4 w-4 ${a.company_saved ? 'fill-accent text-accent' : ''}`} />
                        </button>
                      </div>
                      <button
                        onClick={(e) => { e.stopPropagation(); setRowMenu(rowMenu?.id === a.application_id ? null : { id: a.application_id, rect: e.currentTarget.getBoundingClientRect() }) }}
                        className="shrink-0 rounded-lg p-1.5 text-[#69738A] hover:bg-[#F1F0F7]"
                      >
                        <MoreHorizontal className="h-4 w-4" />
                      </button>
                      {rowMenu?.id === a.application_id && (
                        <RowMenu anchorRect={rowMenu.rect} onClose={() => setRowMenu(null)}>
                          <button onClick={() => { toggleSaved(a); setRowMenu(null) }} className="block w-full px-3.5 py-2 text-left text-sm text-[#19172A] hover:bg-[#F5F3FC]">
                            {a.company_saved ? 'เอาออกจากรายการบันทึก' : 'บันทึกผู้สมัคร'}
                          </button>
                          {COMPANY_SETTABLE_STATUSES.filter((s) => s !== a.status).map((s) => (
                            <button key={s} onClick={() => { changeStatus(a, s); setRowMenu(null) }}
                              className={`block w-full px-3.5 py-2 text-left text-sm hover:bg-[#F5F3FC] ${s === 'REJECTED' ? 'text-rose-600' : 'text-[#19172A]'}`}>
                              {APPLICATION_STATUS_LABELS[s]}
                            </button>
                          ))}
                        </RowMenu>
                      )}
                    </li>
                  )
                })}
              </ul>
            )}
          </div>

          {filtered.length > 0 && (
            <div className="mt-3 flex items-center justify-between text-xs text-[#69738A]">
              <p>แสดง {(page - 1) * PAGE_SIZE + 1} - {Math.min(page * PAGE_SIZE, filtered.length)} จาก {filtered.length} รายการ</p>
              <div className="flex items-center gap-1.5">
                <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="flex h-7 w-7 items-center justify-center rounded-lg border border-[#E7E5FF] disabled:opacity-40">‹</button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
                  <button
                    key={n}
                    onClick={() => setPage(n)}
                    className={`flex h-7 w-7 items-center justify-center rounded-lg text-[11px] font-medium ${n === page ? 'bg-accent text-white' : 'border border-[#E7E5FF] text-[#69738A]'}`}
                  >
                    {n}
                  </button>
                ))}
                <button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} className="flex h-7 w-7 items-center justify-center rounded-lg border border-[#E7E5FF] disabled:opacity-40">›</button>
              </div>
            </div>
          )}
        </div>

        {/* RIGHT — preview panel */}
        <div className="sticky top-24 self-start">
          {!active ? (
            <div className="flex h-64 items-center justify-center rounded-2xl border border-dashed border-[#E1DCF5] bg-[#FAFAFF] text-sm text-[#69738A]">
              เลือกผู้สมัครจากรายการด้านซ้ายเพื่อดูรายละเอียด
            </div>
          ) : (
            <div className="border border-[#E7E5FF] bg-white p-6">
              {/* ── Profile header — สแกนได้เร็ว ไม่ใช้ไอคอนเกินจำเป็น ── */}
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div
                    className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full text-base font-semibold text-white"
                    style={{ background: getColorFromName(active.applicant_name) }}
                  >
                    {active.applicant_name[0]}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h2 className="text-lg font-semibold text-[#19172A]">{active.applicant_name}</h2>
                      <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${STATUS_PILL[active.status]}`}>{APPLICATION_STATUS_LABELS[active.status] ?? active.status}</span>
                    </div>
                    <p className="mt-0.5 text-sm text-[#69738A]">สมัคร {job.title} · {active.province || 'ไม่ระบุจังหวัด'}</p>
                  </div>
                </div>
                {active.assessment_score && (
                  <div className="shrink-0 text-right">
                    <p className="text-2xl font-bold leading-none text-accent">
                      {active.assessment_score.score}<span className="text-sm font-medium text-[#69738A]">/{active.assessment_score.total}</span>
                    </p>
                    <p className="mt-1 text-[11px] text-[#69738A]">คะแนนแบบประเมิน</p>
                  </div>
                )}
              </div>

              {/* Primary actions */}
              <div className="mt-4 flex flex-wrap items-center gap-2">
                <button
                  onClick={() => toggleSaved(active)}
                  className="flex items-center gap-1.5 border border-[#E7E5FF] px-3.5 py-2 text-xs font-medium text-[#19172A] transition hover:border-accent hover:text-accent"
                >
                  <Bookmark className={`h-3.5 w-3.5 ${active.company_saved ? 'fill-accent text-accent' : ''}`} />
                  {active.company_saved ? 'บันทึกแล้ว' : 'บันทึกผู้สมัคร'}
                </button>
                <StatusMenu active={active} onChange={(s) => changeStatus(active, s)} />
              </div>

              {/* Tabs */}
              <div className="mt-5 flex gap-4 overflow-x-auto border-b border-[#E7E7ED]">
                {DETAIL_TABS.map((t) => (
                  <button
                    key={t.key}
                    onClick={() => setDetailTab(t.key)}
                    className={`shrink-0 border-b-2 pb-2.5 text-xs font-medium transition-colors ${
                      detailTab === t.key ? 'border-accent text-accent' : 'border-transparent text-[#69738A] hover:text-[#19172A]'
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>

              <div className="pt-4">
                {detailTab === 'info' && <InfoTab a={active} jobTitle={job.title} onSaveNote={(note) => {
                  setJobApplicationNote(jobId, active.application_id, note)
                    .then((updated) => setApplications((prev) => prev.map((x) => (x.application_id === updated.application_id ? updated : x))))
                    .catch((err) => setToast(err instanceof Error ? err.message : 'บันทึกโน้ตไม่สำเร็จ'))
                }} />}
                {detailTab === 'learning' && <LearningTab learning={active.learning} />}
                {detailTab === 'assessment' && <AssessmentTab a={active} />}
                {detailTab === 'documents' && <DocumentsTab a={active} />}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// ── status change menu ──────────────────────────────────────────────────────
function StatusMenu({ active, onChange }: { active: ApiCompanyApplication; onChange: (s: CompanySettableStatus) => void }) {
  const [rect, setRect] = useState<DOMRect | null>(null)
  return (
    <>
      <button
        onClick={(e) => setRect(rect ? null : e.currentTarget.getBoundingClientRect())}
        className="flex items-center gap-1 bg-accent px-3.5 py-2 text-xs font-semibold text-white transition hover:bg-accent2"
      >
        เปลี่ยนสถานะ <ChevronDown className="h-3.5 w-3.5" />
      </button>
      {rect && (
        <RowMenu anchorRect={rect} onClose={() => setRect(null)}>
          {COMPANY_SETTABLE_STATUSES.filter((s) => s !== active.status).map((s) => (
            <button key={s} onClick={() => { onChange(s); setRect(null) }}
              className={`block w-full px-3.5 py-2 text-left text-sm hover:bg-[#F5F3FC] ${s === 'REJECTED' ? 'text-rose-600' : 'text-[#19172A]'}`}>
              {APPLICATION_STATUS_LABELS[s]}
            </button>
          ))}
        </RowMenu>
      )}
    </>
  )
}

// ── detail tabs ──────────────────────────────────────────────────────────────
function FieldLabel({ children }: { children: React.ReactNode }) {
  return <p className="mb-1 text-[11px] font-medium uppercase tracking-wide text-[#9691AC]">{children}</p>
}

function InfoTab({ a, jobTitle, onSaveNote }: { a: ApiCompanyApplication; jobTitle: string; onSaveNote: (note: string) => void }) {
  const workHistory = a.work_history ?? []
  const education = a.education ?? []
  const skills = a.skills ?? []

  return (
    <div>
      <div className="grid grid-cols-1 gap-x-10 gap-y-6 sm:grid-cols-2">
        <div className="space-y-5">
          <div>
            <FieldLabel>ข้อมูลติดต่อ</FieldLabel>
            <div className="space-y-1 text-sm text-[#19172A]">
              <p>{a.email || '-'}</p>
              <p>{a.phone || '-'}</p>
              <p>{a.province ? `${a.province}, ประเทศไทย` : '-'}</p>
            </div>
          </div>
          {skills.length > 0 && (
            <div>
              <FieldLabel>ทักษะ</FieldLabel>
              <div className="flex flex-wrap gap-x-2 gap-y-1.5">
                {skills.map((s) => (
                  <span key={s} className="text-sm text-[#69738A] after:ml-2 after:text-[#D9D5EC] after:content-['·'] last:after:content-none">{s}</span>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="space-y-5 sm:border-l sm:border-[#EFEFF5] sm:pl-10">
          <div>
            <FieldLabel>ตำแหน่งที่สมัคร</FieldLabel>
            <p className="text-sm font-medium text-[#19172A]">{jobTitle}</p>
          </div>
          <div>
            <FieldLabel>เงินเดือนที่คาดหวัง</FieldLabel>
            <p className="text-sm text-[#19172A]">{a.expected_salary != null ? `฿${Number(a.expected_salary).toLocaleString()} / เดือน` : 'ไม่ระบุ'}</p>
          </div>
          <div>
            <FieldLabel>ประสบการณ์ด้านสายงานโลจิสติกส์</FieldLabel>
            <p className="text-sm text-[#19172A]">{a.experience_range ? EXPERIENCE_RANGE_LABELS[a.experience_range] ?? a.experience_range : 'ไม่ระบุ'}</p>
          </div>
        </div>
      </div>

      <CoverLetterSection a={a} />

      {a.screening_answers && a.screening_answers.length > 0 && (
        <div className="mt-6 border-t border-[#EFEFF5] pt-6">
          <FieldLabel>คำตอบคำถามจากบริษัท</FieldLabel>
          <ul className="mt-2 space-y-2.5">
            {a.screening_answers.map((qa) => (
              <li key={qa.question_id}>
                <p className="text-xs text-[#9691AC]">{qa.question_text}</p>
                <p className="text-sm text-[#19172A]">{typeof qa.answer === 'boolean' ? (qa.answer ? 'ใช่' : 'ไม่ใช่') : String(qa.answer)}</p>
              </li>
            ))}
          </ul>
        </div>
      )}

      {workHistory.length > 0 && (
        <div className="mt-8 border-t border-[#EFEFF5] pt-6">
          <FieldLabel>ประสบการณ์ทำงาน</FieldLabel>
          <ul className="mt-3">
            {workHistory.map((e, i) => {
              const isLast = i === workHistory.length - 1
              return (
                <li key={i} className="relative flex gap-4 pb-6 pl-1 last:pb-0">
                  {!isLast && <span className="absolute left-[7px] top-4 h-[calc(100%-8px)] w-px bg-[#EFEFF5]" />}
                  <span className={`relative mt-1.5 h-3.5 w-3.5 shrink-0 rounded-full border-2 ${e.current ? 'border-accent bg-accent' : 'border-[#D9D5EC] bg-white'}`} />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-baseline justify-between gap-x-3">
                      <p className="text-[15px] font-semibold text-[#19172A]">{e.position || '-'}</p>
                      <span className={`shrink-0 text-xs font-medium ${e.current ? 'text-accent' : 'text-[#9691AC]'}`}>
                        {e.current ? 'Current' : formatMonthYear(e.end_date)}
                      </span>
                    </div>
                    <p className="text-sm text-[#69738A]">{e.company || '-'}</p>
                    <p className="mt-0.5 text-xs text-[#9691AC]">
                      {formatMonthYear(e.start_date)} — {e.current ? 'Present' : formatMonthYear(e.end_date)}
                    </p>
                    {e.description && <p className="mt-1.5 text-sm leading-relaxed text-[#69738A]">{e.description}</p>}
                  </div>
                </li>
              )
            })}
          </ul>
        </div>
      )}

      {education.length > 0 && (
        <div className="mt-2 border-t border-[#EFEFF5] pt-6">
          <FieldLabel>การศึกษา</FieldLabel>
          <ul className="mt-3 space-y-4">
            {education.map((ed, i) => (
              <li key={i}>
                <p className="text-[15px] font-semibold text-[#19172A]">{ed.university || '-'}</p>
                <p className="text-sm text-[#69738A]">{[ed.faculty, ed.major].filter(Boolean).join(' • ')}</p>
                <p className="mt-0.5 text-xs text-[#9691AC]">
                  {ed.gpa != null && `เกรดเฉลี่ย ${ed.gpa} • `}{ed.graduated ? 'จบการศึกษาแล้ว' : 'คาดว่าจะจบ'} {formatMonthYear(ed.graduation_date)}
                </p>
              </li>
            ))}
          </ul>
        </div>
      )}

      <NoteSection note={a.company_note} onSave={onSaveNote} />
    </div>
  )
}

function LearningHeading({ title, count }: { title: string; count: number }) {
  return (
    <div className="mb-3 flex items-baseline gap-2 border-b border-[#EFEFF5] pb-2">
      <h4 className="text-[15px] font-semibold text-[#19172A]">{title}</h4>
      <span className="text-xs text-[#9691AC]">{count} รายการ</span>
    </div>
  )
}

// แท็บ "สกิลและการอบรม" — สกิลคือหลักฐานว่าเรียนจบคอร์สจริงใน JOS CoWork (ต่างจาก "ทักษะ" ที่ผู้สมัครพิมพ์เอง)
function LearningTab({ learning }: { learning?: ApiCompanyApplication['learning'] }) {
  const skills = learning?.skills ?? []
  const trainings = learning?.trainings ?? []
  const formatDate = (iso?: string | null) => (iso ? new Date(iso).toLocaleDateString('th-TH', { day: 'numeric', month: 'long', year: 'numeric' }) : '-')

  if (learning === undefined) return <p className="py-8 text-center text-sm text-[#9691AC]">กำลังโหลด...</p>
  if (learning === null) return <p className="py-8 text-center text-sm text-[#9691AC]">ไม่สามารถโหลดสกิลและประวัติการอบรมได้ในขณะนี้</p>
  if (skills.length === 0 && trainings.length === 0) {
    return <p className="py-8 text-center text-sm text-[#9691AC]">ผู้สมัครยังไม่มีสกิลหรือประวัติการอบรมจาก JOS CoWork</p>
  }

  return (
    <div className="space-y-9">
      <section>
        <LearningHeading title="สกิลที่ได้รับ" count={skills.length} />
        {skills.length === 0 ? (
          <p className="text-sm text-[#9691AC]">ยังไม่มีสกิล</p>
        ) : (
          <ul className="space-y-3">
            {skills.map((s) => {
              const Icon = getSkillIcon(s.icon)
              const percent = s.total > 0 ? Math.min(100, Math.round((s.done / s.total) * 100)) : 0
              return (
                <li key={s.skill_id} className="rounded-lg border border-[#EFEFF5] p-4">
                  <div className="flex items-center gap-3">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-accent/10 text-accent">
                      <Icon className="h-5 w-5" strokeWidth={1.7} />
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-[15px] font-semibold leading-snug text-[#19172A]">{s.name}</p>
                      <p className="text-xs text-[#9691AC]">หมวด {s.category_name}</p>
                    </div>
                    {s.full && <span className="shrink-0 rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-medium text-emerald-700">ครบทุกคอร์ส</span>}
                  </div>
                  <div className="mt-3 flex items-center gap-3">
                    <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-[#EFEFF5]">
                      <div className={`h-full rounded-full ${s.full ? 'bg-emerald-500' : 'bg-accent'}`} style={{ width: `${percent}%` }} />
                    </div>
                    <span className="shrink-0 text-xs tabular-nums text-[#69738A]">เรียนจบ {s.done} จาก {s.total} คอร์ส</span>
                  </div>
                </li>
              )
            })}
          </ul>
        )}
      </section>

      <section>
        <LearningHeading title="ประวัติการเรียนและการอบรม" count={trainings.length} />
        {trainings.length === 0 ? (
          <p className="text-sm text-[#9691AC]">ยังไม่มีประวัติการอบรม</p>
        ) : (
          <ul className="space-y-3">
            {trainings.map((t, i) => (
              <li key={i} className="rounded-lg border border-[#EFEFF5] p-4">
                <div className="flex items-start justify-between gap-3">
                  <p className="text-[15px] font-semibold leading-snug text-[#19172A]">{t.title}</p>
                  <span className={`shrink-0 rounded-full px-2.5 py-1 text-[11px] font-medium ${t.type === 'ONLINE' ? 'bg-accent/10 text-accent' : 'bg-amber-50 text-amber-700'}`}>
                    {t.type === 'ONLINE' ? 'คอร์สออนไลน์' : 'อบรม Onsite'}
                  </span>
                </div>
                <dl className="mt-2 grid grid-cols-[110px_1fr] gap-y-1 text-sm">
                  {t.type === 'ONLINE' ? (
                    <>
                      <dt className="text-[#9691AC]">เรียนจบเมื่อ</dt>
                      <dd className="text-[#19172A]">{formatDate(t.date)}</dd>
                      {t.certificate_number && (
                        <>
                          <dt className="text-[#9691AC]">ใบประกาศนียบัตร</dt>
                          <dd className="text-[#19172A]">{t.certificate_number}</dd>
                        </>
                      )}
                    </>
                  ) : (
                    <>
                      {t.batch_name && (
                        <>
                          <dt className="text-[#9691AC]">รุ่นอบรม</dt>
                          <dd className="text-[#19172A]">{t.batch_name}</dd>
                        </>
                      )}
                      <dt className="text-[#9691AC]">วันที่อบรม</dt>
                      <dd className="text-[#19172A]">
                        {formatDate(t.date)}{t.end_date && t.end_date !== t.date ? ` – ${formatDate(t.end_date)}` : ''}
                      </dd>
                    </>
                  )}
                </dl>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  )
}

// จดหมายแนะนำตัว — ย่อไว้ 2-3 บรรทัดแล้วค่อยกด "อ่านเพิ่มเติม"
function CoverLetterSection({ a }: { a: ApiCompanyApplication }) {
  const [expanded, setExpanded] = useState(false)
  return (
    <div className="mt-6 border-t border-[#EFEFF5] pt-6">
      <FieldLabel>จดหมายแนะนำตัว</FieldLabel>
      {a.cover_letter ? (
        <div className="mt-1">
          <p className={`text-sm leading-relaxed text-[#19172A] ${expanded ? '' : 'line-clamp-3'}`}>{a.cover_letter}</p>
          {a.cover_letter.length > 120 && (
            <button onClick={() => setExpanded((v) => !v)} className="mt-1.5 text-xs font-medium text-accent hover:underline">
              {expanded ? 'ย่อข้อความ' : 'อ่านเพิ่มเติม'}
            </button>
          )}
        </div>
      ) : a.documents.some((d) => d.kind === 'COVER_LETTER') ? (
        <p className="text-sm text-[#69738A]">แนบเป็นไฟล์ — ดูได้ที่แท็บ "เอกสารแนบ"</p>
      ) : (
        <p className="text-sm text-[#69738A]">ผู้สมัครไม่ได้แนบจดหมายแนะนำตัว</p>
      )}
    </div>
  )
}

// โน้ตภายใน — บริษัทเห็นเท่านั้น ผู้สมัครไม่เห็น
function NoteSection({ note, onSave }: { note: string | null; onSave: (note: string) => void }) {
  const [value, setValue] = useState(note ?? '')
  const [saving, setSaving] = useState(false)
  const changed = value.trim() !== (note ?? '')

  const save = () => {
    setSaving(true)
    Promise.resolve(onSave(value.trim())).finally(() => setSaving(false))
  }

  return (
    <div className="mt-8 border-t border-[#EFEFF5] pt-6">
      <FieldLabel>โน้ตภายใน (บริษัทเห็นเท่านั้น)</FieldLabel>
      <textarea
        value={value}
        onChange={(e) => setValue(e.target.value)}
        rows={3}
        maxLength={2000}
        placeholder="บันทึกข้อสังเกตเกี่ยวกับผู้สมัครคนนี้..."
        className="mt-1 w-full resize-none rounded-lg border border-[#E7E5FF] px-3 py-2 text-sm text-[#19172A] outline-none focus:border-accent"
      />
      {changed && (
        <button onClick={save} disabled={saving}
          className="mt-2 flex items-center gap-1.5 rounded-lg bg-accent px-3.5 py-1.5 text-xs font-semibold text-white transition hover:bg-accent2 disabled:opacity-60">
          <StickyNote className="h-3.5 w-3.5" /> {saving ? 'กำลังบันทึก...' : 'บันทึกโน้ต'}
        </button>
      )}
    </div>
  )
}

function formatSpentTime(seconds: number): string {
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  if (m === 0) return `${s} วินาที`
  return s === 0 ? `${m} นาที` : `${m} นาที ${s} วินาที`
}

function AssessmentTab({ a }: { a: ApiCompanyApplication }) {
  const result = a.assessment_score
  if (!result) {
    return <p className="text-sm text-[#69738A]">ผู้สมัครยังไม่ได้ทำแบบประเมินก่อนสมัครงาน (หรือแบบประเมินยังไม่เปิดใช้งานตอนที่สมัคร)</p>
  }
  const percentOf = (score: number, total: number) => (total > 0 ? Math.round((score / total) * 100) : 0)
  const pct = percentOf(result.score ?? 0, result.total)
  const sections = result.section_scores ?? []
  return (
    <div className="space-y-4">
      <p className="text-xs text-[#69738A]">ผลการประเมินเป็นข้อมูลประกอบการพิจารณา ไม่ใช่เกณฑ์ตัดสินหลัก</p>
      <div className="flex items-center gap-4 rounded-lg border border-[#E7E5FF] px-4 py-3">
        <p className="text-3xl font-bold text-accent">{result.score}<span className="text-base font-medium text-[#69738A]">/{result.total}</span></p>
        <div>
          <p className="text-sm font-semibold text-[#19172A]">{pct}%</p>
          <p className="text-xs text-[#69738A]">แบบประเมินก่อนสมัครงาน</p>
        </div>
      </div>

      {result.time_spent_seconds != null && (
        <div className="flex items-center justify-between rounded-lg border border-[#E7E5FF] px-4 py-3 text-sm">
          <span className="text-[#69738A]">เวลาที่ใช้ทำแบบประเมิน</span>
          <span className="font-semibold text-[#19172A]">
            {formatSpentTime(result.time_spent_seconds)}
            {result.time_limit_minutes ? <span className="font-normal text-[#69738A]"> (จากเวลาทั้งหมด {result.time_limit_minutes} นาที)</span> : null}
          </span>
        </div>
      )}

      {sections.length > 0 && (
        <div className="rounded-lg border border-[#E7E5FF] px-4 py-3">
          <p className="mb-3 text-sm font-semibold text-[#19172A]">คะแนนแยกตามส่วน</p>
          <ul className="space-y-3">
            {sections.map((s) => {
              const p = percentOf(s.score, s.total)
              return (
                <li key={s.code}>
                  <div className="mb-1 flex items-center justify-between text-sm">
                    <span className="text-[#19172A]">{s.title}</span>
                    <span className="font-semibold text-[#19172A]">{s.score}/{s.total} <span className="font-normal text-[#69738A]">({p}%)</span></span>
                  </div>
                  <div className="h-1.5 overflow-hidden rounded-full bg-[#F1F0F7]">
                    <div className="h-full rounded-full bg-accent" style={{ width: `${p}%` }} />
                  </div>
                </li>
              )
            })}
          </ul>
        </div>
      )}
    </div>
  )
}

function DocumentsTab({ a }: { a: ApiCompanyApplication }) {
  const [error, setError] = useState('')
  const docs = a.documents ?? []

  if (docs.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-[#E7E5FF] bg-[#FAFAFF] p-5 text-center">
        <FileText className="mx-auto mb-2 h-5 w-5 text-[#9691AC]" />
        <p className="text-sm text-[#69738A]">ผู้สมัครไม่ได้แนบเอกสาร</p>
      </div>
    )
  }
  return (
    <div className="space-y-2">
      {docs.map((d) => (
        <button key={d.id} type="button"
          onClick={() => { setError(''); downloadApplicationDocument(a.application_id, d).catch((e) => setError(e instanceof Error ? e.message : 'ดาวน์โหลดไฟล์ไม่สำเร็จ')) }}
          className="flex w-full items-center gap-2.5 rounded-lg border border-[#E7E5FF] px-3.5 py-2.5 text-left text-sm text-[#19172A] transition hover:border-accent hover:text-accent">
          <FileText className="h-4 w-4 text-accent" /> {d.label}
          <span className="ml-auto text-xs uppercase text-[#69738A]">{d.ext}</span>
        </button>
      ))}
      {error && <p className="text-xs text-rose-500">{error}</p>}
    </div>
  )
}
